import { toCalendarIsoDay } from "./tradeDashboardDates";
import {
  isCancelledTrade,
  isClosedTrade,
  getTradeCloseIso,
} from "./dashboardStatsUtils";

function isAgentDraft(trade) {
  return (
    trade?.createdByRole === "agent" &&
    !trade?.lockedBySuperUser &&
    trade?.workflowStatus !== "approved"
  );
}

function countableTrade(trade) {
  if (!trade || isCancelledTrade(trade)) return false;
  return !isAgentDraft(trade);
}

/**
 * Period date for agent performance: when the trade was closed in-office.
 * Prefer finalized / closed-TRS dates so Monthly Performers matches deals
 * finalized that month (not commission-received date).
 */
function getPerformerPeriodIso(trade) {
  if (!isClosedTrade(trade)) return null;
  const k = trade?.keyInfo || {};
  return (
    toCalendarIsoDay(k.finalizedDate) ||
    toCalendarIsoDay(k.closedTRSDate) ||
    toCalendarIsoDay(trade?.progressTracker?.closedTRSDate) ||
    getTradeCloseIso(trade)
  );
}

function agentKey(row) {
  const id = String(row?.agentId ?? "").trim();
  const name = String(row?.agentName ?? "").trim().toLowerCase();
  return id || name;
}

function agentLabel(row) {
  return String(row?.agentName ?? "").trim() || `Agent ${row?.agentId || ""}`.trim();
}

function rowCommission(row) {
  return (
    parseFloat(row?.amount || row?.awardAmount || row?.netCommission || 0) || 0
  );
}

function periodMatches(iso, year, month) {
  if (!iso) return false;
  if (month == null) return iso.startsWith(String(year));
  const prefix = `${year}-${String(month + 1).padStart(2, "0")}`;
  return iso.startsWith(prefix);
}

export function buildPerformerRanks(trades, { year, month } = {}) {
  const map = new Map();
  for (const trade of trades || []) {
    if (!countableTrade(trade)) continue;
    const periodIso = getPerformerPeriodIso(trade);
    if (!periodMatches(periodIso, year, month)) continue;
    const rows = trade.agentCommissionList || [];
    if (!rows.length) continue;
    const seenOnTrade = new Set();
    for (const row of rows) {
      const key = agentKey(row);
      if (!key) continue;
      const existing = map.get(key) || {
        id: key,
        name: agentLabel(row),
        commission: 0,
        trades: 0,
      };
      existing.commission += rowCommission(row);
      if (!seenOnTrade.has(key)) {
        existing.trades += 1;
        seenOnTrade.add(key);
      }
      if (!existing.name && agentLabel(row)) existing.name = agentLabel(row);
      map.set(key, existing);
    }
  }
  return [...map.values()]
    .filter((row) => row.commission > 0 || row.trades > 0)
    .sort((a, b) => {
      if (b.commission !== a.commission) return b.commission - a.commission;
      return b.trades - a.trades;
    });
}

export function performerValue(row, metric) {
  if (!row) return 0;
  return metric === "trades" ? row.trades : row.commission;
}

export function topPerformers(trades, period, metric = "commission", limit = 8) {
  return [...buildPerformerRanks(trades, period)]
    .sort((a, b) => {
      const av = performerValue(a, metric);
      const bv = performerValue(b, metric);
      if (bv !== av) return bv - av;
      return b.commission - a.commission;
    })
    .slice(0, limit);
}
